// Chip rendering for `document`/`entity`/`citation` inline components, and a dashed-underline
// text-link treatment for `user` (see below), decoded from a `run-completed` event's
// `finalAnswer.answerMarkdown` (see `../../services/inlineComponents/decodeInlineComponentStream.js`).
//
// Entity markers render like Product Editor linked entities: blue dashed underline +
// MentionHoverCard on hover; click opens EntityInfoDrawer via the chart AI bridge
// (see `AIAssistantChatPanel.jsx` handleSelectComponent). Linked mentions are also
// HTML5-draggable onto the chart canvas (same MIME as WhiteboardSearchTab).
import React from 'react';
import {Box, Chip, Link, Tooltip} from '@mui/material';
import DescriptionOutlinedIcon from '@mui/icons-material/DescriptionOutlined';
import FormatQuoteIcon from '@mui/icons-material/FormatQuote';
import AccountTreeOutlinedIcon from '@mui/icons-material/AccountTreeOutlined';
import MenuBookOutlinedIcon from '@mui/icons-material/MenuBookOutlined';
import MentionHoverCard from '../../../../components/entityMentions/MentionHoverCard';
import {
    entityDisplayLabel,
    resolveEntityRefFromPayload,
} from '../../services/inlineComponents/decodeInlineComponentStream';
import {writeEntityCardDragData} from '../../../whiteboards/tldraw/customShapes/entityCardDrag';

const ICON_BY_TYPE = {
    document: DescriptionOutlinedIcon,
};

// Inline (not inline-flex / inline-block): the mark can split across lines like text, and
// box-decoration-break:clone paints background/padding on each fragment. MUI Chip cannot do this.
const CITATION_MARK_WRAP_STYLE = {
    display: 'inline',
    boxDecorationBreak: 'clone',
    WebkitBoxDecorationBreak: 'clone',
    fontSize: 'inherit',
    lineHeight: 'inherit',
};

function getChipLabel(type, payload) {
    if (payload && typeof payload === 'object') {
        const label = payload.title || payload.name || payload.label || payload.reportTitle;
        if (label) {
            return String(label);
        }
        if (type === 'citation' && payload.reportId) {
            return 'FINTEL';
        }
        if (type !== 'entity') {
            const id = payload.id;
            if (id) {
                return String(id);
            }
        }
    }
    return type;
}

function getChipTooltip(payload) {
    if (!payload || typeof payload !== 'object') {
        return '';
    }
    return payload.excerpt || payload.description || payload.summary || '';
}

function getUserLabel(payload) {
    if (payload && typeof payload === 'object') {
        return String(payload.displayName || payload.userId || 'user');
    }
    return 'user';
}

function EntityInlineLink({payload, onSelect}) {
    const label = entityDisplayLabel(payload) || getChipLabel('entity', payload);

    const entityRef = resolveEntityRefFromPayload(payload);
    const entityType = payload?.entityType || null;
    const clickable = Boolean(onSelect);
    const draggable = Boolean(entityRef);
    const spanRef = React.useRef(null);
    const dragOccurredRef = React.useRef(false);

    const handleDragStart = React.useCallback((event) => {
        if (!writeEntityCardDragData(event.dataTransfer, payload)) {
            return;
        }
        dragOccurredRef.current = true;
        event.stopPropagation();
    }, [payload]);

    // Chromium excludes permanently-draggable nodes from Selection.toString() / copy.
    // Arm HTML5 drag only for the gesture so a select-and-copy of the answer keeps labels.
    const armHtmlDrag = React.useCallback((event) => {
        if (!draggable || event.button !== 0) {
            return;
        }
        event.currentTarget.setAttribute('draggable', 'true');
    }, [draggable]);

    const disarmHtmlDrag = React.useCallback((event) => {
        event.currentTarget.removeAttribute('draggable');
    }, []);

    const handleDragEnd = React.useCallback((event) => {
        event.currentTarget.removeAttribute('draggable');
        window.setTimeout(() => {
            dragOccurredRef.current = false;
        }, 0);
    }, []);

    const openEntity = React.useCallback((event) => {
        if (!clickable || dragOccurredRef.current) {
            return;
        }
        event.preventDefault();
        event.stopPropagation();
        onSelect({type: 'entity', payload});
    }, [clickable, onSelect, payload]);

    if (!label || label === 'entity') {
        return null;
    }

    return (
        <MentionHoverCard
            name={label}
            entityType={entityType}
            entityRef={entityRef || null}
            mentionAnchorRef={spanRef}
        >
            <span
                ref={spanRef}
                role={clickable ? 'button' : undefined}
                tabIndex={clickable ? 0 : undefined}
                className="wikidata-entity wikidata-entity--linked"
                onMouseDown={draggable ? armHtmlDrag : undefined}
                onMouseUp={draggable ? disarmHtmlDrag : undefined}
                onDragStart={draggable ? handleDragStart : undefined}
                onDragEnd={draggable ? handleDragEnd : undefined}
                onClick={openEntity}
                onKeyDown={clickable ? (event) => {
                    if (event.key === 'Enter' || event.key === ' ') {
                        openEntity(event);
                    }
                } : undefined}
                data-id={entityRef || undefined}
                data-wikidata-id={entityRef || undefined}
                style={{
                    userSelect: 'text',
                    WebkitUserSelect: 'text',
                    ...(draggable ? {cursor: 'grab'} : null),
                }}
            >
                {label}
            </span>
        </MentionHoverCard>
    );
}

function getSourceChipLabel(type, payload) {
    if (payload && typeof payload === 'object') {
        if (payload.label) {
            return String(payload.label);
        }
        if (type === 'citation') {
            return getChipLabel('citation', payload);
        }
        if (type === 'chart') {
            return payload.chartTitle || payload.title || 'Chart';
        }
        if (type === 'kb') {
            if (payload.propertyLabel && payload.entityLabel) {
                return `${payload.entityLabel} · ${payload.propertyLabel}`;
            }
            return payload.propertyLabel || payload.entityLabel || 'KB field';
        }
    }
    return type;
}

function getSourceChipTooltip(type, payload) {
    if (!payload || typeof payload !== 'object') {
        return '';
    }
    if (type === 'citation') {
        return getChipTooltip(payload);
    }
    if (type === 'chart') {
        return payload.chartTitle || payload.title || '';
    }
    if (type === 'kb') {
        return payload.value || payload.propertyLabel || '';
    }
    return '';
}

function SourceInlineMark({type, payload, onSelect, testIdAttr}) {
    const label = getSourceChipLabel(type, payload);
    const tooltip = getSourceChipTooltip(type, payload);
    const clickable = Boolean(onSelect);

    const activate = React.useCallback((event) => {
        if (!clickable) {
            return;
        }
        event.preventDefault();
        event.stopPropagation();
        onSelect({type, payload});
    }, [clickable, onSelect, payload, type]);

    const Icon = type === 'chart'
        ? AccountTreeOutlinedIcon
        : (type === 'kb' ? MenuBookOutlinedIcon : FormatQuoteIcon);

    const mark = (
        <Box
            component="span"
            data-inline-citation=""
            data-inline-source-type={type}
            role={clickable ? 'button' : undefined}
            tabIndex={clickable ? 0 : undefined}
            onClick={clickable ? activate : undefined}
            onKeyDown={clickable ? (event) => {
                if (event.key === 'Enter' || event.key === ' ') {
                    activate(event);
                }
            } : undefined}
            data-testid={testIdAttr}
            style={CITATION_MARK_WRAP_STYLE}
            sx={{
                px: 0.5,
                py: 0,
                mx: 0.25,
                bgcolor: 'action.selected',
                color: 'text.primary',
                borderRadius: 1,
                fontWeight: 500,
                cursor: clickable ? 'pointer' : 'default',
                verticalAlign: 'baseline',
                '&:hover': clickable ? {bgcolor: 'action.hover'} : undefined,
            }}
        >
            <Icon
                aria-hidden
                sx={{
                    fontSize: '1em',
                    width: '1em',
                    height: '1em',
                    verticalAlign: '-0.125em',
                    mr: 0.25,
                }}
            />
            {label}
        </Box>
    );

    return tooltip ? <Tooltip title={tooltip}>{mark}</Tooltip> : mark;
}

function CitationInlineMark({payload, onSelect}) {
    return (
        <SourceInlineMark
            type="citation"
            payload={payload}
            onSelect={onSelect}
        />
    );
}

/** @param {{type: string, payload: unknown, onSelect?: (component: {type: string, payload: unknown}) => void}} props */
const InlineComponentChip = ({type, payload, onSelect}) => {
    if (type === 'user') {
        const label = getUserLabel(payload);
        if (!label || label === 'user') {
            return null;
        }
        const clickable = Boolean(onSelect);
        return (
            <Link
                component="span"
                onClick={clickable ? () => onSelect({type, payload}) : undefined}
                sx={{
                    textDecoration: '1px dashed underline',
                    color: clickable ? 'primary.main' : 'text.primary',
                    borderColor: 'primary.main',
                    cursor: clickable ? 'pointer' : 'default',
                }}
            >
                {label}
            </Link>
        );
    }

    if (type === 'entity') {
        return <EntityInlineLink payload={payload} onSelect={onSelect}/>;
    }

    if (type === 'citation') {
        return <CitationInlineMark payload={payload} onSelect={onSelect}/>;
    }

    if (type === 'chart') {
        return <SourceInlineMark type={type} payload={payload} onSelect={onSelect}/>;
    }

    if (type === 'kb') {
        return null;
    }

    const Icon = ICON_BY_TYPE[type] || DescriptionOutlinedIcon;
    const label = getChipLabel(type, payload);
    const tooltip = getChipTooltip(payload);

    const chip = (
        <Chip
            component="span"
            size="small"
            icon={<Icon fontSize="inherit"/>}
            label={label}
            onClick={onSelect ? () => onSelect({type, payload}) : undefined}
            clickable={Boolean(onSelect)}
            sx={{
                mx: 0.375,
                verticalAlign: 'middle',
                fontWeight: 500,
                maxWidth: 220,
            }}
        />
    );

    return tooltip ? <Tooltip title={tooltip}>{chip}</Tooltip> : chip;
};

export default InlineComponentChip;
